import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { Observable, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { UserAccountModel, LookupItem, AccountUpdateRequest } from '../../models/account.model';
import {
  AccountFormConfig,
  DEFAULT_LANGUAGES,
  DEFAULT_SHORT_TIMES,
} from '../../configuration/account-form.config';
import { getValidationMessage, VALIDATION_MESSAGES } from '../../constant/validation-messages';
import { AccountService } from '../../services/account.service';
import { DynamicFormComponent } from '../forms/dynamic-form/dynamic-form.component';
import {
  DynamicFormValue,
  DynamicFormConfig,
} from '../forms/dynamic-form/models/dynamic-form.model';
import {
  BaseFormComponent,
  FormMode,
} from '../../../core/components/base-form/base-form.component';

@Component({
  selector: 'app-account-form',
  standalone: true,
  imports: [CommonModule, DynamicFormComponent],
  templateUrl: './account-form.component.html',
  styleUrl: './account-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountFormComponent extends BaseFormComponent<UserAccountModel, AccountUpdateRequest> {
  @Input() accountData?: UserAccountModel;

  private readonly accountService = inject(AccountService);

  timezones: LookupItem[] = [];

  private userId = 0;

  protected override initializeForm(): void {
    this.isDialogMode = !!this.dialogRef;

    const dialogData = this.dialogRef?.content?.instance?.data;
    this.userId = dialogData?.userId ?? 0;

    this.loadAccountData();
  }

  private loadAccountData(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.accountService
      .getAccountById(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.messageType === 0 && response?.data) {
            this.accountData = response.data;
            this.timezones = response.data.timeZones ?? [];

            this.formMode = this.determineFormMode(this.accountData);
            this.formConfig = this.buildFormConfig(this.formMode);
            this.formConfig.mode = this.isDialogMode ? 'popup' : 'page';
            this.formConfig.showActions = false;
            this.initialValues = this.mapEntityToFormValues(response.data);
          } else {
            this.notification.error(
              getValidationMessage(response?.message, VALIDATION_MESSAGES.FAILED_TO_LOAD_DATA)
            );
          }

          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading account data:', error);
          this.notification.error(VALIDATION_MESSAGES.FAILED_TO_LOAD_DATA);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected getEntityData(): UserAccountModel | null {
    return this.accountData ?? null;
  }

  protected buildFormConfig(_mode: FormMode): DynamicFormConfig {
    return AccountFormConfig.getEditConfig(
      this.timezones,
      DEFAULT_LANGUAGES,
      DEFAULT_SHORT_TIMES
    );
  }

  protected mapEntityToFormValues(account: UserAccountModel): DynamicFormValue {
    return {
      id: account.id,
      firstName: account.firstName,
      lastName: account.lastName,
      email: account.email,
      language: 1, // default english
      timeZone: account.timeZone,
      shortTime: account.shortTime,
    };
  }

  protected preparePayload(formValue: DynamicFormValue): AccountUpdateRequest {
    return {
      id: this.accountData?.id ?? this.userId,
      firstName: formValue['firstName'] as string,
      lastName: formValue['lastName'] as string,
      email: formValue['email'] as string,
      language: formValue['language'] as number | null,
      timeZone: formValue['timeZone'] as number | null,
      shortTime: formValue['shortTime'] as number | null,
    };
  }

  protected save(payload: AccountUpdateRequest): Observable<any> {
    return this.accountService.updateAccount(payload);
  }

  protected getEntityName(): string {
    return 'Account';
  }

  protected override submitForm(formValue: DynamicFormValue): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const payload = this.preparePayload(formValue);

    this.save(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;

          const messageType = response?.messageType;

          if (messageType === 0) {
            this.notification.success('Account updated successfully');
            this.formSubmit.emit(formValue);

            if (this.dialogRef) {
              this.dialogRef.close({
                success: true,
                action: 'save',
                data: response?.data ?? payload,
              });
            }
          } else {
            this.notification.error(VALIDATION_MESSAGES.FAILED_TO_SAVE_DATA);
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isLoading = false;
          const errorMessage = error?.error?.message ?? VALIDATION_MESSAGES.FAILED_TO_SAVE_DATA;
          this.notification.error(errorMessage);
          this.cdr.markForCheck();
        },
      });
  }
}
